/**
 * The Documents explorer's filter/pagination state and its URL persistence.
 *
 * The categorical dimensions share the Dashboard's param vocabulary (`type`,
 * `location`, `scanner`, `source`, `start`, `end`) so a drill-through from a
 * dashboard chart arrives with its filter already applied. The explorer adds its
 * own controls — a `status` segment, a free-text `q` search, and a 0-based
 * `page` — all URL-persisted so a view is shareable and survives a reload. The
 * backend query names (`location_id`, `scanner_id`) are applied only at the API
 * edge by {@link toListParams}.
 */
import type { ExportRequestBody, ListDocumentsParams } from './client';

/** The status-segment keys shown as chips (design-reference §4.3). */
export type StatusKey = 'all' | 'classified' | 'needs_review' | 'processing' | 'failed';

export const STATUS_KEYS: readonly StatusKey[] = [
  'all',
  'classified',
  'needs_review',
  'processing',
  'failed',
];

const STATUS_SET = new Set<string>(STATUS_KEYS);

export interface DocFilters {
  type?: string;
  location?: string;
  scanner?: string;
  source?: string;
  start?: string;
  end?: string;
  /** Status segment; `all` means unfiltered (and is omitted from the URL). */
  status: StatusKey;
  /** Free-text filename search. */
  q?: string;
  /** 0-based page index. */
  page: number;
  /** Rows per page; one of {@link PAGE_SIZES}. */
  perPage: number;
}

/**
 * The default rows per page.
 *
 * Ten, deliberately small: the table body is a fixed-height scroll region, so a
 * page that overflows it is a page the reader has to scroll to finish. Ten fits
 * without scrolling on a laptop, and 25/50/100 are a click away for anyone who
 * would rather scroll than paginate.
 */
export const PAGE_SIZE = 10;

/**
 * The rows-per-page choices. Validated against on parse, so a hand-edited or
 * stale URL cannot ask the API for an arbitrary page size — the list endpoint has
 * its own cap, but a shared link should not be able to request 5,000 rows either.
 */
export const PAGE_SIZES: readonly number[] = [10, 25, 50, 100];

/** The categorical dimensions that also come from the dashboard drill-through. */
export const DIMENSION_KEYS = ['type', 'location', 'scanner', 'source'] as const;

const STRING_KEYS = ['type', 'location', 'scanner', 'source', 'start', 'end', 'q'] as const;

/** Read a filter set from a URL query string. Blank values are dropped. */
export function parseFilters(params: URLSearchParams): DocFilters {
  const out: DocFilters = { status: 'all', page: 0, perPage: PAGE_SIZE };
  for (const key of STRING_KEYS) {
    const value = params.get(key);
    if (value !== null && value !== '') out[key] = value;
  }
  const status = params.get('status');
  if (status !== null && STATUS_SET.has(status)) out.status = status as StatusKey;
  const page = Number(params.get('page'));
  if (Number.isInteger(page) && page > 0) out.page = page;
  const perPage = Number(params.get('per'));
  if (PAGE_SIZES.includes(perPage)) out.perPage = perPage;
  return out;
}

/** Serialise a filter set to query params — omit empty keys, deterministic order. */
export function serializeFilters(filters: DocFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of STRING_KEYS) {
    const value = filters[key];
    if (value !== undefined && value !== '') params.set(key, value);
  }
  if (filters.status !== 'all') params.set('status', filters.status);
  if (filters.page > 0) params.set('page', String(filters.page));
  if (filters.perPage !== PAGE_SIZE) params.set('per', String(filters.perPage));
  return params;
}

/** Map a filter set to the list endpoint's query shape (`*_id`, skip/limit). */
export function toListParams(filters: DocFilters, pageSize = filters.perPage): ListDocumentsParams {
  const out: ListDocumentsParams = { skip: filters.page * pageSize, limit: pageSize };
  if (filters.type !== undefined) out.type = filters.type;
  if (filters.location !== undefined) out.location_id = filters.location;
  if (filters.scanner !== undefined) out.scanner_id = filters.scanner;
  if (filters.source !== undefined) out.source = filters.source;
  if (filters.start !== undefined) out.start = filters.start;
  if (filters.end !== undefined) out.end = filters.end;
  if (filters.q !== undefined) out.q = filters.q;
  if (filters.status !== 'all') out.status = filters.status;
  return out;
}

/** Map a filter set to the export endpoint's body — every filter, no pagination. */
export function toExportParams(filters: DocFilters): ExportRequestBody {
  const out: ExportRequestBody = {};
  if (filters.type !== undefined) out.type = filters.type;
  if (filters.location !== undefined) out.location_id = filters.location;
  if (filters.scanner !== undefined) out.scanner_id = filters.scanner;
  if (filters.source !== undefined) out.source = filters.source;
  if (filters.start !== undefined) out.start = filters.start;
  if (filters.end !== undefined) out.end = filters.end;
  if (filters.q !== undefined) out.q = filters.q;
  if (filters.status !== 'all') out.status = filters.status;
  return out;
}

/** True when any categorical dimension, status or search is active. */
export function hasActiveFilters(filters: DocFilters): boolean {
  return (
    filters.status !== 'all' ||
    filters.q !== undefined ||
    DIMENSION_KEYS.some((key) => filters[key] !== undefined && filters[key] !== '')
  );
}

/** Reset everything except the (drill-through) date range and the page size. */
export function clearedFilters(filters: DocFilters): DocFilters {
  // `perPage` survives deliberately: it is a display preference, not a filter, so
  // "Clear all" should not silently put the reader back on 25 rows.
  return {
    start: filters.start,
    end: filters.end,
    status: 'all',
    page: 0,
    perPage: filters.perPage,
  };
}

/** Set one categorical dimension, returning to page 0. */
export function withDimension(
  filters: DocFilters,
  key: (typeof DIMENSION_KEYS)[number],
  value: string | undefined,
): DocFilters {
  const next: DocFilters = { ...filters, page: 0 };
  next[key] = value === undefined || value === '' ? undefined : value;
  return next;
}

/** Set the status segment, returning to page 0. */
export function withStatus(filters: DocFilters, status: StatusKey): DocFilters {
  return { ...filters, status, page: 0 };
}

/** Set the search query, returning to page 0. Empty clears it. */
export function withQuery(filters: DocFilters, q: string): DocFilters {
  const next: DocFilters = { ...filters, page: 0 };
  if (q.trim() === '') delete next.q;
  else next.q = q;
  return next;
}

/** Change the page index (clamped at 0). */
export function withPage(filters: DocFilters, page: number): DocFilters {
  return { ...filters, page: Math.max(0, page) };
}

/**
 * Change the page size, returning to the first page.
 *
 * The reset is the point: page 7 of 10-row pages is row 70, which does not exist
 * once the size is 100 — the reader would ask for a size and be shown an empty
 * table. Keeping the row they were looking at in view would be nicer still, but
 * "start again from the top" is at least never wrong.
 */
export function withPerPage(filters: DocFilters, perPage: number): DocFilters {
  return { ...filters, perPage, page: 0 };
}
